import { AuthService } from 'src/app/services/auth.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { hashCode, INDIAN_STATES } from 'src/app/global';
import { Route, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import * as Global from 'src/app/global';

/* ============================================================
   Static data
   ============================================================ */

interface StepDef {
  id: number;
  label: string;
}

const STEPS: StepDef[] = [
  { id: 1, label: 'Account details' },
  { id: 2, label: 'Business verification' },
  { id: 3, label: 'Company details' },
  { id: 4, label: 'Bank details' },
  { id: 5, label: 'Locations' },
  { id: 6, label: 'Digital signature' },
  { id: 7, label: 'Review & Signup' },
];

const PDF_EXTENSIONS = ['.pdf'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

/* ============================================================
   Validators
   ============================================================ */

export function passwordsMatchValidator(
  passwordKey: string,
  confirmKey: string,
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const passwordControl = group.get(passwordKey);
    const confirmControl = group.get(confirmKey);

    // If controls don't exist yet, do nothing
    if (!passwordControl || !confirmControl) {
      return null;
    }

    const password = passwordControl.value;
    const confirm = confirmControl.value;

    // Skip validation if fields are empty (let 'required' validator handle blanks)
    if (!password || !confirm) {
      return null;
    }

    if (password !== confirm) {
      // Dynamically attach the error directly to the confirm password field
      confirmControl.setErrors({
        ...confirmControl.errors,
        passwordMismatch: true,
      });
      return { passwordMismatch: true };
    } else {
      // Clear the error if they now match, preserving any other existing errors
      if (confirmControl.hasError('passwordMismatch')) {
        const errors = { ...confirmControl.errors };
        delete errors['passwordMismatch'];
        confirmControl.setErrors(Object.keys(errors).length ? errors : null);
      }
      return null;
    }
  };
}

function fileRequiredValidator(
  control: AbstractControl,
): ValidationErrors | null {
  return control.value ? null : { fileRequired: true };
}

function hasValidExtension(file: File, allowedExtensions: string[]): boolean {
  const name = (file.name || '').toLowerCase();
  return allowedExtensions.some((ext) => name.endsWith(ext));
}

function fileTypeValidator(allowedExtensions: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const file = control.value as File | null;
    if (!file) {
      // empty value is handled by fileRequiredValidator where applicable
      return null;
    }
    return hasValidExtension(file, allowedExtensions)
      ? null
      : { fileType: true };
  };
}

/* ============================================================
   Component
   ============================================================ */

@Component({
  selector: 'app-seller-onboard',
  templateUrl: './seller-onboard.component.html',
  styleUrls: ['./seller-onboard.component.css'],
})
export class SellerOnboardComponent implements OnInit, OnDestroy {
  readonly steps: StepDef[] = STEPS;
  readonly states: string[] = INDIAN_STATES;

  form!: FormGroup;

  step = 1;
  touched = false;
  submitted = false;

  gstVerifying = false;
  gstVerified = false;

  logoPreview: string | null = null;
  isDragOver: Record<string, boolean> = {};
  INDIAN_STATES = INDIAN_STATES;
  sellerId = '';
  Global = Global;

  readonly PDF_EXTENSIONS = PDF_EXTENSIONS;
  readonly IMAGE_EXTENSIONS = IMAGE_EXTENSIONS;

  private destroy$ = new Subject<void>();
  private gstTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.watchSameAsShip();
    this.form
      .get('company_info.company_gst_no')
      ?.valueChanges.subscribe((gst) => {
        if (gst) {
          this.form.patchValue({
            company_info: {
              company_pan_no: gst.substring(2, 12).toUpperCase(),
            },
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.gstTimer) {
      clearTimeout(this.gstTimer);
    }
  }

  // ---------- form construction ----------

  private buildForm(): void {
    this.form = this.fb.group({
      account: this.fb.group(
        {
          name: ['', Validators.required],
          mobile: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
          email: ['', [Validators.required, Validators.email]],
          password: ['', [Validators.required, Validators.minLength(8)]],
          confirmPassword: ['', Validators.required],
        },
        { validators: passwordsMatchValidator('password', 'confirmPassword') },
      ),

      company_info: this.fb.group({
        company_gst_no: [
          '',
          [Validators.required, Validators.pattern(/^.{15}$/)],
        ],
        company_pan_no: [
          '',
          [
            Validators.required,
            Validators.pattern(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/),
          ],
        ],
        company_name: ['', Validators.required],
        company_address: ['', Validators.required],
      }),

      company: this.fb.group({
        tmco: [''],
        tmco_file: [null, fileTypeValidator(PDF_EXTENSIONS)],
        logo: [
          null,
          [fileRequiredValidator, fileTypeValidator(IMAGE_EXTENSIONS)],
        ],
        gardens: ['', Validators.required],
      }),

      bank: this.fb.group({
        accountHolder: ['', Validators.required],
        bankName: ['', Validators.required],
        branch: ['', Validators.required],
        accountNumber: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
        ifsc: ['', Validators.required],
        cheque: [null, fileTypeValidator(IMAGE_EXTENSIONS)],
      }),

      locations: this.fb.group({
        ship_to_address: ['', Validators.required],
        ship_city: ['', Validators.required],
        ship_state: ['', Validators.required],
        ship_pincode: [
          '',
          [Validators.required, Validators.pattern(/^\d{6}$/)],
        ],
        sameAsShip: [false],
        bill_to_address: ['', Validators.required],
        bill_city: ['', Validators.required],
        bill_state: ['', Validators.required],
        bill_pincode: [
          '',
          [Validators.required, Validators.pattern(/^\d{6}$/)],
        ],
      }),

      signature: this.fb.group({
        // Digital signature: PDF only
        signatureFile: [null, fileTypeValidator(PDF_EXTENSIONS)],
      }),

      review: this.fb.group({
        agreeTerms: [false, Validators.requiredTrue],
      }),
    });
  }

  private watchSameAsShip(): void {
    const locations = this.locationsGroup;
    locations
      .get('sameAsShip')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((sameAsShip: boolean) => this.syncFactoryFields(sameAsShip));

    ['ship_to_address', 'ship_city', 'ship_state', 'ship_pincode'].forEach(
      (field) => {
        locations
          .get(field)!
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (locations.get('sameAsShip')!.value) {
              this.syncFactoryFields(true);
            }
          });
      },
    );
  }

  private syncFactoryFields(sameAsShip: boolean): void {
    const locations = this.locationsGroup;
    const factoryFields = [
      'bill_to_address',
      'bill_city',
      'bill_state',
      'bill_pincode',
    ];
    const shipFields = [
      'ship_to_address',
      'ship_city',
      'ship_state',
      'ship_pincode',
    ];

    if (sameAsShip) {
      factoryFields.forEach((field, i) => {
        locations
          .get(field)!
          .setValue(locations.get(shipFields[i])!.value, { emitEvent: false });
        locations.get(field)!.disable({ emitEvent: false });
      });
    } else {
      factoryFields.forEach((field) =>
        locations.get(field)!.enable({ emitEvent: false }),
      );
    }
  }

  // ---------- convenience getters ----------

  get accountGroup(): FormGroup {
    return this.form.get('account') as FormGroup;
  }
  get companyInfoGroup(): FormGroup {
    return this.form.get('company_info') as FormGroup;
  }
  get companyGroup(): FormGroup {
    return this.form.get('company') as FormGroup;
  }
  get bankGroup(): FormGroup {
    return this.form.get('bank') as FormGroup;
  }
  get locationsGroup(): FormGroup {
    return this.form.get('locations') as FormGroup;
  }
  get signatureGroup(): FormGroup {
    return this.form.get('signature') as FormGroup;
  }
  get reviewGroup(): FormGroup {
    return this.form.get('review') as FormGroup;
  }

  get passwordMismatch(): boolean {
    return (
      this.accountGroup.hasError('passwordMismatch') &&
      !!this.accountGroup.get('confirmPassword')?.value
    );
  }

  // ---------- sidebar progress (display only) ----------

  /** Percentage of steps completed, shown under the tea cup. */
  get progressPct(): number {
    return Math.round(((this.step - 1) / this.steps.length) * 100);
  }

  /** Liquid surface Y position in the cup SVG: 178 (empty) → 70 (full). */
  get cupLevel(): number {
    return 178 - 108 * ((this.step - 1) / this.steps.length);
  }

  // ---------- GST verification ----------

  verifyGst(): void {
    this.gstVerified = true;
    const payload = {
      gst_no: this.form.get('company_info.company_gst_no')?.value,
    };
  }

  // ---------- generic file-drop handling (reused by every upload field) ----------

  handleFileInput(
    event: Event,
    control: AbstractControl,
    allowedExtensions?: string[],
  ): void {
    const input = event.target as HTMLInputElement;
    const picked = input.files && input.files.length ? input.files[0] : null;
    if (picked) {
      if (allowedExtensions && !hasValidExtension(picked, allowedExtensions)) {
        this.toastr.error(
          `Please upload a ${allowedExtensions.join('/').toUpperCase()} file`,
        );
        input.value = '';
        return;
      }
      control.setValue(picked);
    }
    input.value = '';
  }

  onDragOver(event: DragEvent, key: string): void {
    event.preventDefault();
    this.isDragOver[key] = true;
  }

  onDragLeave(event: DragEvent, key: string): void {
    event.preventDefault();
    this.isDragOver[key] = false;
  }

  onFileDrop(
    event: DragEvent,
    control: AbstractControl,
    key: string,
    allowedExtensions?: string[],
  ): void {
    event.preventDefault();
    this.isDragOver[key] = false;
    const dropped =
      event.dataTransfer?.files && event.dataTransfer.files.length
        ? event.dataTransfer.files[0]
        : null;
    if (dropped) {
      if (allowedExtensions && !hasValidExtension(dropped, allowedExtensions)) {
        this.toastr.error(
          `Please upload a ${allowedExtensions.join('/').toUpperCase()} file`,
        );
        return;
      }
      control.setValue(dropped);
    }
  }

  removeFile(control: AbstractControl): void {
    control.setValue(null);
  }

  // ---------- logo (needs a base64 preview, so handled separately) ----------

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (file) {
      if (!hasValidExtension(file, IMAGE_EXTENSIONS)) {
        this.toastr.error(
          `Please upload a ${IMAGE_EXTENSIONS.join('/').toUpperCase()} file`,
        );
        input.value = '';
        return;
      }
      this.setLogo(file);
    }
    input.value = '';
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver['logo'] = false;
    const file =
      event.dataTransfer?.files && event.dataTransfer.files.length
        ? event.dataTransfer.files[0]
        : null;
    if (file) {
      if (!hasValidExtension(file, IMAGE_EXTENSIONS)) {
        this.toastr.error(
          `Please upload a ${IMAGE_EXTENSIONS.join('/').toUpperCase()} file`,
        );
        return;
      }
      this.setLogo(file);
    }
  }

  private setLogo(file: File | null): void {
    this.companyGroup.get('logo')!.setValue(file);
    if (!file) {
      this.logoPreview = null;
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => (this.logoPreview = e.target?.result as string);
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    this.setLogo(null);
  }

  // ---------- signature (PDF only) ----------

  onSignatureFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (file && !hasValidExtension(file, PDF_EXTENSIONS)) {
      this.toastr.error('Please upload a PDF file');
      input.value = '';
      return;
    }
    this.signatureGroup.get('signatureFile')!.setValue(file);
    input.value = '';
  }

  removeSignatureFile(): void {
    this.signatureGroup.get('signatureFile')!.setValue(null);
  }

  // ---------- step navigation ----------

  private currentGroup(): FormGroup | null {
    switch (this.step) {
      case 1:
        return this.accountGroup;
      case 3:
        return this.companyGroup;
      case 4:
        return this.bankGroup;
      case 5:
        return this.locationsGroup;
      default:
        return null;
    }
  }

  isStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return this.accountGroup.valid;
      case 2:
        return this.companyInfoGroup.valid;
      case 3:
        return this.companyGroup.valid;
      case 4:
        return this.bankGroup.valid;
      case 5:
        return this.locationsGroup.valid;
      case 6:
        return (
          !!this.signatureGroup.get('signatureFile')!.value &&
          this.signatureGroup.valid
        );
      default:
        return true;
    }
  }

  isStepDone(stepId: number): boolean {
    return stepId < this.step;
  }

  async goNext() {
    if (!this.isStepValid(this.step)) {
      this.touched = true;
      const group = this.currentGroup();
      group?.markAllAsTouched();
      return;
    }
    if (this.step == 1) {
      const is_unique = await this.checkuniqueEmail();
      if (!is_unique) {
        this.toastr.clear();
        this.toastr.error('This email already taken.');
        return;
      }
    }
    this.touched = false;
    this.step = Math.min(this.step + 1, 7);
  }

  goBack(): void {
    this.touched = false;
    this.step = Math.max(this.step - 1, 1);
  }

  goToStep(step: number): void {
    this.step = step;
  }

  // ---------- review / submit ----------

  get canSubmit(): boolean {
    return this.reviewGroup.get('agreeTerms')!.value;
  }

  joinFilled(values: Array<string | null | undefined>): string {
    const filled = (values || []).filter((v) => !!v);
    return filled.length ? filled.join(', ') : '—';
  }

  submit(event: any): void {
    this.submitted = true;
    if (this.form.valid) {
      event.target.classList.add('btn-loading');
      event.target.disabled = true;
      const {
        account: { name, mobile, email, password },
        company_info,
        company: { tmco, tmco_file, logo, gardens },
        bank: { accountHolder, bankName, branch, accountNumber, ifsc, cheque },
        locations: {
          ship_to_address,
          ship_city,
          ship_state,
          ship_pincode,
          bill_to_address,
          bill_city,
          bill_state,
          bill_pincode,
        },
        signature: { signatureFile },
      } = this.form.getRawValue();
      const payload: any = {
        name,
        mobile,
        email,
        password,
        company_info: JSON.stringify(company_info),
        tmco,
        tmco_file,
        logo,
        gardens,
        accountHolder,
        bankName,
        branch,
        accountNumber,
        ifsc,
        cheque,
        ship_to_address,
        ship_city,
        ship_state,
        ship_pincode,
        bill_to_address,
        bill_city,
        bill_state,
        bill_pincode,
        signatureFile,
      };

      this.authService
        .sellerRegister(payload)
        .pipe(
          finalize(() => {
            event.target.classList.remove('btn-loading');
            event.target.disabled = false;
          }),
        )
        .subscribe({
          next: (res) => {
            const data = res.data;
            if (res.status == 'success') {
              this.authService.userSuccessLogin({
                token: data?.token ?? null,
                user: data?.user ?? null,
              });
            } else if (res.status == 'otpverification') {
              this.router.navigate([
                `/otp-verification/seller-register/${res.data.token}`,
              ]);
              this.toastr.success(res.message);
            } else if (res.status == 'val_error') {
              this.toastr.error(
                Global.getValidationMessage(data?.errors ?? []),
              );
            } else {
              this.toastr.error(res.message);
            }
          },
          error: (err) => {
            // Handle error
          },
        });
    }
  }

  // ═══════════════════ template helpers ═══════════════════

  isInvalid(path: string): boolean {
    const c = this.form.get(path);
    return !!c && c.invalid && this.submitted;
  }

  checkuniqueEmail() {
    return new Promise((resolve, reject) => {
      this.authService
        .checkuniqueEmail({
          email: this.form?.value?.account?.email,
          role: 'seller',
        })
        .subscribe((res) => {
          if (res.status == 'success') {
            resolve(true);
          } else {
            resolve(false);
          }
        });
    });
  }
}
